import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatDate, formatCurrency } from '../utils/dateUtils';
import { StatusBadge } from '../components/common/Badge';
import { Wrench, ExternalLink, Search } from 'lucide-react';

export const Services = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('service_records')
        .select('*, vehicles(id,registration_number,brand,model)')
        .order('service_date', { ascending: false });
      setRecords(data || []);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    return !q
      || r.vehicles?.registration_number?.toLowerCase().includes(q)
      || r.garage_name?.toLowerCase().includes(q);
  });

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Service History</div>
          <div className="page-subtitle">{records.length} service records fleet-wide</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
          <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Search by registration or garage…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <Wrench />
            <h3>No service records</h3>
            <p>Add service records from a vehicle's detail page.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Service Date</th>
                  <th>Garage</th>
                  <th>Mileage</th>
                  <th>Cost</th>
                  <th>Next Service</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/vehicles/${r.vehicles?.id}`)}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: 1, textTransform: 'uppercase' }}>
                          {r.vehicles?.registration_number}
                        </span>
                        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 500, color: 'var(--text2)' }}>
                          {r.vehicles?.brand} {r.vehicles?.model}
                        </span>
                      </div>
                    </td>
                    <td>{formatDate(r.service_date)}</td>
                    <td className="td-primary">{r.garage_name}</td>
                    <td>{r.mileage_at_service ? r.mileage_at_service.toLocaleString() + ' km' : '—'}</td>
                    <td>{formatCurrency(r.cost)}</td>
                    <td>{formatDate(r.next_service_date)}</td>
                    <td><StatusBadge date={r.next_service_date} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      {r.invoice_url && (
                        <a href={r.invoice_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
